import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import { AddRounded, DeleteOutlineRounded, EditRounded, ViewTimelineRounded } from '@mui/icons-material';
import { FlowBreadcrumbs, FlowPageHeader, FlowStatGrid, SoftPanel, mediaBaseUrl, truncateText } from './FlowLayout';
import { fetchDestinationHierarchy } from './flowHierarchy';

const ManageItinerary = () => {
  const { destinations_id } = useParams();
  const [itineraries, setItineraries] = useState([]);
  const [hierarchy, setHierarchy] = useState({
    destinationName: '',
    tourTypeId: '',
    tourTypeName: '',
    continentId: '',
    continentName: ''
  });
  const [selectedItineraryId, setSelectedItineraryId] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`https://aaryanholidays.in/backend/itineraries/${destinations_id}`)
      .then((response) => setItineraries(response.data))
      .catch((error) => console.error('There was an error fetching the itineraries!', error));
    fetchDestinationHierarchy(destinations_id)
      .then((response) => setHierarchy(response))
      .catch((error) => console.error('There was an error fetching the destination context!', error));
  }, [destinations_id]);

  const filteredItineraries = useMemo(() => {
    return itineraries.filter((item) =>
      String(item.tab_heading || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [itineraries, search]);

  const handleDelete = (id) => {
    axios.delete(`https://aaryanholidays.in/backend/itineraries/${id}`)
      .then(() => setItineraries((current) => current.filter((itinerary) => itinerary.id !== id)))
      .catch((error) => console.error('There was an error deleting the itinerary!', error));
    setOpenConfirmDialog(false);
    setSelectedItineraryId(null);
  };

  return (
    <Box>
      <FlowBreadcrumbs
        items={[
          { label: 'Tour Management', to: '/manage-continents' },
          { label: hierarchy.continentName || 'Continent', to: hierarchy.continentId ? `/manage-tour-type/${hierarchy.continentId}` : undefined },
          { label: hierarchy.tourTypeName || 'Country', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined },
          { label: hierarchy.destinationName || 'Package', to: hierarchy.tourTypeId ? `/manage-destination/${hierarchy.tourTypeId}` : undefined },
          { label: 'Itineraries' }
        ]}
      />

      <FlowPageHeader
        title="Manage Itineraries"
        subtitle="Keep tab content, sequence, and supporting visuals easy to review and update."
        search={search}
        searchPlaceholder="Search tab headings or itinerary descriptions"
        onSearchChange={setSearch}
        action={(
          <Button
            component={Link}
            to={`/manage-itinerary/add/${destinations_id}`}
            variant="contained"
            startIcon={<AddRounded />}
            sx={{ borderRadius: 999, px: 2.5, py: 1.2, textTransform: 'none', fontWeight: 700 }}
          >
            Add Itinerary
          </Button>
        )}
      />

      <FlowStatGrid
        items={[
          { label: 'Itinerary Tabs', value: itineraries.length, icon: ViewTimelineRounded, tone: 'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)' }
        ]}
      />

      <SoftPanel>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Tab Heading</TableCell>
                <TableCell>Description Heading</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Image</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItineraries.map((itinerary) => (
                <TableRow key={itinerary.id} hover>
                  <TableCell>{itinerary.id}</TableCell>
                  <TableCell>{itinerary.tab_heading}</TableCell>
                  <TableCell>{itinerary.description_heading}</TableCell>
                  <TableCell>{truncateText(itinerary.description, 110)}</TableCell>
                  <TableCell>{itinerary.image ? <img src={`${mediaBaseUrl}${itinerary.image}`} alt={itinerary.tab_heading} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }} /> : '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit itinerary">
                      <IconButton component={Link} to={`/manage-itinerary/edit/${itinerary.id}/${destinations_id}`}>
                        <EditRounded />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete itinerary">
                      <IconButton onClick={() => { setSelectedItineraryId(itinerary.id); setOpenConfirmDialog(true); }}>
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SoftPanel>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this itinerary? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Cancel</Button>
          <Button onClick={() => handleDelete(selectedItineraryId)} autoFocus>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageItinerary;
